import type { TransactionListResponse } from '@/api/transactions'
import { useListAmountFormat } from '@/hooks/useFormatCurrency'
import { DESKTOP_QUERY, useMediaQuery } from '@/hooks/useMediaQuery'
import { formatShortDate } from '@/lib/formatDate'
import { LEAD_LABEL, leadFor, periodLabel } from './transactionDisplay'
import { fromPaise, toPaise } from '@/lib/splitMath'
import type { TransactionLinkFilters } from '@/lib/transactionsLink'
import { cn } from '@/lib/utils'

function signed(format: (n: number) => string, n: number): string {
  if (n > 0) return `+${format(n)}`
  if (n < 0) return `−${format(-n)}`
  return format(0)
}

export function TransactionsHero({
  data,
  filters,
  scopeLabels,
}: {
  data?: TransactionListResponse
  filters: TransactionLinkFilters
  /** Names of the active filters (category, account, person…) shown after the period. */
  scopeLabels: string[]
}) {
  const format = useListAmountFormat()
  const wide = useMediaQuery(DESKTOP_QUERY)
  const lead = leadFor(filters)
  // spent/income are optional: the web app can be live before the API that adds them.
  const spent = data?.spent ?? data?.totalExpenses ?? 0
  const income = data?.income ?? data?.totalIncome ?? 0
  const capitalSpent = data?.capitalSpent ?? 0
  const capitalIncome = data?.capitalIncome ?? 0
  const capitalCount = data?.capitalCount ?? 0

  const leadValue =
    lead === 'spent'
      ? format(spent)
      : lead === 'income'
        ? format(income)
        : lead === 'oneoff'
          ? signed(format, fromPaise(toPaise(capitalIncome) - toPaise(capitalSpent)))
          : signed(format, fromPaise(toPaise(income) - toPaise(spent)))

  const today = new Date().toISOString().slice(0, 10)
  const daily = data?.daily && data.daily.length > 1 ? data.daily : null
  const metric = lead === 'income' ? 'income' : 'spent'
  const elapsed = daily?.filter((d) => d.date <= today) ?? []
  const maxDay = Math.max(1, ...(daily ?? []).map((d) => d[metric]))
  const perDay =
    lead === 'spent' && elapsed.length > 0
      ? fromPaise(Math.round(toPaise(spent) / elapsed.length))
      : null

  const side: { label: string; value: string; tone?: 'in' | 'out' }[] =
    lead === 'net'
      ? [
          { label: 'Spent', value: format(spent), tone: 'out' },
          { label: 'Income', value: format(income), tone: 'in' },
        ]
      : lead === 'oneoff'
        ? [
            { label: 'Spent', value: format(capitalSpent), tone: 'out' },
            { label: 'Income', value: format(capitalIncome), tone: 'in' },
          ]
        : perDay != null
          ? [{ label: 'Per day', value: format(perDay) }]
          : []

  const showOneOff = lead !== 'oneoff' && capitalCount > 0
  const period = [periodLabel(filters), ...scopeLabels].join(' · ')
  const leadFigure = (
    <p
      data-testid="hero-lead"
      className="shrink-0 font-serif text-[22px] font-bold leading-tight tabular-nums tracking-tight md:mt-0.5 md:text-[40px] md:font-semibold"
    >
      {leadValue}
    </p>
  )

  return (
    <section
      aria-label="Summary"
      className="flex flex-col gap-2 rounded-2xl border border-[var(--fl-hero-border)] bg-gradient-to-br from-[var(--fl-hero-bg)] to-[var(--fl-hero-bg-2)] px-3.5 py-3 text-[var(--fl-hero-fg)] md:gap-4 md:rounded-[20px] md:p-6"
    >
      {wide ? (
        <p className="text-[11.5px] font-semibold uppercase tracking-[0.08em] text-[var(--fl-hero-fg-2)]">
          {period}
        </p>
      ) : (
        <div className="flex items-baseline justify-between gap-3">
          <p className="min-w-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--fl-hero-fg-2)]">
            {period} · <span>{LEAD_LABEL[lead]}</span>
          </p>
          {leadFigure}
        </div>
      )}
      <div className="flex flex-wrap items-end justify-between gap-3">
        {wide && (
          <div>
            <p className="text-xs font-medium text-[var(--fl-hero-fg-2)]">{LEAD_LABEL[lead]}</p>
            {leadFigure}
          </div>
        )}
        {side.length > 0 && (
          <dl className="flex gap-4 md:items-end md:gap-6">
            {side.map((s) => (
              <div
                key={s.label}
                className="flex items-baseline gap-1.5 text-[13px] md:gap-2 md:text-[12.5px]"
              >
                <dt className="text-[var(--fl-hero-fg-2)]">{s.label}</dt>
                <dd
                  className={cn(
                    'font-semibold tabular-nums md:font-serif md:text-lg',
                    s.tone === 'out' && 'text-[var(--fl-hero-expense)]',
                    s.tone === 'in' && 'text-[var(--fl-hero-income)]'
                  )}
                >
                  {s.value}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </div>

      {showOneOff && (
        <div
          data-testid="hero-one-off"
          className="flex flex-wrap items-baseline justify-between gap-2 border-t border-[var(--fl-hero-bar-faint)] pt-3"
        >
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--fl-hero-fg-2)]">
              One-off · not counted above
            </p>
            <p className="text-[12.5px] text-[var(--fl-hero-fg-2)]">
              {capitalCount === 1 ? '1 event' : `${capitalCount} events`}
            </p>
          </div>
          <div className="flex gap-4 text-[12.5px]">
            {capitalSpent !== 0 && (
              <span className="flex items-baseline gap-1.5">
                <span className="text-[var(--fl-hero-fg-2)]">Spent</span>
                <span className="font-serif text-base font-semibold tabular-nums text-[var(--fl-hero-expense)]">
                  {format(capitalSpent)}
                </span>
              </span>
            )}
            {capitalIncome !== 0 && (
              <span className="flex items-baseline gap-1.5">
                <span className="text-[var(--fl-hero-fg-2)]">Income</span>
                <span className="font-serif text-base font-semibold tabular-nums text-[var(--fl-hero-income)]">
                  {format(capitalIncome)}
                </span>
              </span>
            )}
          </div>
        </div>
      )}

      {daily && (
        <div className="flex flex-col gap-1.5">
          <div
            role="img"
            aria-label={`${metric === 'income' ? 'Income' : 'Spending'} per day`}
            className="flex h-8 items-end gap-[3px] md:h-12"
          >
            {daily.map((d) => {
              const value = d[metric]
              const future = d.date > today
              return (
                <div
                  key={d.date}
                  className={cn(
                    'flex-1 rounded-sm',
                    !future && value > 0
                      ? 'bg-[var(--fl-hero-bar)]'
                      : 'bg-[var(--fl-hero-bar-faint)]'
                  )}
                  style={{
                    height: `${Math.max(4, (Math.max(0, value) / maxDay) * 100)}%`,
                  }}
                />
              )
            })}
          </div>
          {wide && (
            <div className="flex justify-between text-[11px] text-[var(--fl-hero-fg-2)]">
              <span>{formatShortDate(daily[0].date)}</span>
              <span>{formatShortDate(daily[daily.length - 1].date)}</span>
            </div>
          )}
        </div>
      )}
    </section>
  )
}
